import React, { ReactNode, cloneElement, isValidElement } from 'react';
import { useFeatureFlags } from '@/providers/FeatureFlagsProvider';
import { type FeatureFlag, getDefaultFlags } from '@/config/featureFlags';
import ComingSoonModal from '@/components/ComingSoonModal';

interface FeatureGateProps {
  flag: FeatureFlag;
  children: ReactNode;
  fallback?: ReactNode;
  showComingSoon?: boolean;
}

/**
 * FeatureGate component that conditionally renders children based on feature flags.
 * 
 * When flag is disabled:
 * - Shows fallback (if provided) or nothing
 * - Can show ComingSoonModal if showComingSoon=true
 * - Prevents any side-effects from running
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({
  flag,
  children,
  fallback = null,
  showComingSoon = false,
}) => {
  const { flags, loading } = useFeatureFlags();
  const [showModal, setShowModal] = React.useState(false);

  // Show loading state or defaults while fetching
  if (loading) {
    return <>{fallback}</>;
  }

  const isEnabled = flags[flag];

  if (!isEnabled) {
    if (showComingSoon && fallback && isValidElement(fallback)) {
      return (
        <>
          {cloneElement(fallback as React.ReactElement, {
            onClick: (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              setShowModal(true);
            },
          })}
          <ComingSoonModal 
            open={showModal} 
            onClose={() => setShowModal(false)}
            feature={flag}
          />
        </>
      );
    }
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * Hook to check if a specific feature is enabled
 */
export const useFeatureEnabled = (flag: FeatureFlag): boolean => {
  const { flags, loading } = useFeatureFlags();
  if (loading) {
    // Use getDefaultFlags() which includes hostname-based overrides,
    // not raw env vars which may be absent from the compiled bundle.
    return getDefaultFlags()[flag];
  }
  return flags[flag];
};

